import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useCallback, useRef, useEffect } from 'react';
import { BufferedEventBus } from './BufferedEventBus';
import { APP_FOREGROUNDED } from '../AppContainer';

export function useElapsedTimer(onElapsed: () => void, ms: number) {
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const startTimeRef = useRef<number | null>(null);
    const remainingRef = useRef<number>(ms);
    const finishedRef = useRef<boolean>(false);

    const eventBus = useInstance(BufferedEventBus);

    const clear = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    };

    const checkElapsed = useCallback(() => {
        if (finishedRef.current || startTimeRef.current === null) {
            return;
        }
        const now = Date.now();
        const elapsed = now - startTimeRef.current;
        if (elapsed >= remainingRef.current) {
            finishedRef.current = true;
            clear();
            onElapsed();
        } else {
            // update remaining and restart timer
            remainingRef.current = remainingRef.current - elapsed;
            startTimeRef.current = now;
            clear();
            timerRef.current = setTimeout(() => {
                finishedRef.current = true;
                timerRef.current = null;
                onElapsed();
            }, remainingRef.current);
        }
    }, [onElapsed]);

    const startTimer = useCallback(() => {
        if (timerRef.current || finishedRef.current) {
            return;
        }
        startTimeRef.current = Date.now();
        timerRef.current = setTimeout(() => {
            finishedRef.current = true;
            timerRef.current = null;
            onElapsed();
        }, remainingRef.current);
    }, [onElapsed]);

    const stopTimer = useCallback(() => {
        if (timerRef.current && startTimeRef.current !== null) {
            const now = Date.now();
            remainingRef.current = remainingRef.current - (now - startTimeRef.current);
        }
        clear();
        startTimeRef.current = null;
    }, []);

    // Listen for APP_FOREGROUNDED event from BufferedEventBus
    useEffect(() => {
        const handleAppForegrounded = () => {
            checkElapsed();
        };
        const unsub = eventBus.on(APP_FOREGROUNDED, handleAppForegrounded);
        return () => {
            unsub();
            clear();
        };
    }, [eventBus, checkElapsed]);

    // Optionally, reset finishedRef and remainingRef if ms changes
    useEffect(() => {
        finishedRef.current = false;
        remainingRef.current = ms;
        clear();
        startTimeRef.current = null;
    }, [ms]);

    return { startTimer, stopTimer };
};
